/**
 * Voie b) « Recherche » — unifiée base personnelle + Open Food Facts
 * (specs §6.3-b et §7.2).
 *
 * ⚠️ **La base personnelle est toujours en premier, et visuellement distinguée**
 * (specs §6.3-b). Le serveur a déjà retiré des résultats OFF ceux que la base
 * couvre (même code-barres, même nom et même marque) : le client n'a rien à
 * arbitrer, il affiche dans l'ordre.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 🔴 DEUX MOITIÉS, DEUX SOURCES (V2, étape 4)
 * ─────────────────────────────────────────────────────────────────────────
 *
 * | Moitié | D'où elle vient | Hors ligne |
 * |-|-|-|
 * | base personnelle, recettes | **la réplique en cache**, filtrée en mémoire | ✅ |
 * | Open Food Facts | le serveur, qui seul y accède (§5.2) | ❌, et le dit |
 *
 * C'est très exactement le contrat du §3.1 : « la moitié personnelle du
 * résultat s'affiche normalement ; la moitié OFF annonce l'indisponibilité ».
 * Jusqu'à cette étape, la moitié personnelle passait **elle aussi** par le
 * réseau : hors ligne, l'écran n'affichait donc rien du tout, alors que les
 * aliments cherchés étaient à quelques octets de là.
 *
 * ⚠️ **Le filtrage local est fait TOUJOURS, pas seulement hors ligne.** Deux
 * chemins auraient produit un comportement intermittent — le défaut que le banc
 * de l'étape 2 traque nommément. Ici il ne se passe simplement rien de
 * différent, et le classement vient de `shared/search.ts`, le même module que
 * la route du serveur : l'ordre ne bouge pas non plus.
 *
 * ⚠️ Conséquence assumée : `GET /api/foods/search` continue de renvoyer ses
 * trois listes, dont cet écran n'emploie plus que `off`. La route reste
 * l'implémentation de référence du classement et répond toujours la même chose
 * à un appel direct — c'est l'écran qui n'a plus besoin de la moitié qu'il peut
 * calculer lui-même.
 *
 * ⚠️ **Débounce de 400 ms** (§5.2) : il ne retarde plus que l'appel **OFF**.
 * Les résultats personnels, eux, sortent à la frappe — ils ne coûtent plus
 * d'aller-retour à espacer.
 *
 * ⚠️ Quand OFF est injoignable, la recherche **ne casse pas** : la base
 * personnelle répond, et une ligne discrète explique l'absence de la seconde
 * moitié. Une erreur bloquante ici rendrait l'app inutilisable hors connexion
 * alors que l'essentiel — les aliments déjà connus — est en local.
 *
 * ⚠️ **Les recettes sont cherchées ici aussi** (étape 7), en tête des trois
 * listes. Extension assumée du §6.3-b, qui ne parle que d'aliments : sans elle,
 * une recette ni favorite ni récente serait inatteignable depuis l'écran
 * d'ajout, l'accès rapide ne montrant que deux listes courtes.
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';

import { fetchFoods, searchFoods } from '../../api/foods';
import { fetchRecipes, RECIPES_KEY } from '../../api/recipes';
import { Button } from '../../components/nourri';
import { ErrorNote, Hint } from '../../components/ui/primitives';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { searchFoodsLocally, searchRecipesLocally } from '../../lib/localSearch';
import type { AddPick } from './addTypes';
import { OffFoodRow, PersonalFoodRow, PersonalRecipeRow } from './FoodRow';

/** En deçà, le serveur n'interroge pas OFF : autant ne pas l'appeler non plus. */
const MIN_QUERY_LENGTH = 2;

function Heading({ children }: { children: string }) {
  return (
    <h3 className="n-eyebrow" style={{ margin: '4px 0 0' }}>
      {children}
    </h3>
  );
}

export function SearchPanel({
  scope,
  onPick,
  onCreate,
}: {
  /**
   * Ce qu'on cherche. La recherche unifiée du serveur renvoie toujours les trois
   * listes ; l'écran n'affiche que celles de sa catégorie.
   *
   * ⚠️ Filtrer à l'affichage plutôt que côté serveur est délibéré : la réponse
   * est déjà en cache, et basculer de « Aliment » à « Recette » sur la même
   * saisie ne coûte alors aucun aller-retour.
   */
  scope: 'food' | 'recipe';
  onPick: (pick: AddPick) => void;
  /** Créer une fiche de toutes pièces quand rien ne correspond. */
  onCreate: () => void;
}) {
  const [term, setTerm] = useState('');
  const cherche = term.trim();
  const debounced = useDebouncedValue(cherche);

  /*
   * Les deux répliques (§3.1) — **les mêmes entrées de cache** que la
   * Bibliothèque et le scanner. Une base, un cache, une vérité : leur donner
   * des clés propres à cet écran ferait deux copies dont l'une pourrait être
   * plus fraîche que l'autre.
   *
   * ⚠️ Elles sont persistées, donc présentes au démarrage même sans réseau, et
   * elles ne dépendent pas du terme cherché : c'est ce qui rend la recherche
   * possible sur un mot jamais tapé en ligne.
   */
  const foods = useQuery({ queryKey: ['foods', ''], queryFn: () => fetchFoods('') });
  const recipes = useQuery({ queryKey: RECIPES_KEY, queryFn: () => fetchRecipes('') });

  const personal = useMemo(
    () => (cherche === '' ? [] : searchFoodsLocally(foods.data ?? [], cherche)),
    [foods.data, cherche],
  );
  const localRecipes = useMemo(
    () => (cherche === '' ? [] : searchRecipesLocally(recipes.data ?? [], cherche)),
    [recipes.data, cherche],
  );

  /*
   * ⚠️ **Cette requête n'est plus consultée que pour sa moitié `off`.** Elle
   * garde son débounce et son plancher de longueur, qui existent pour épargner
   * des appels à Open Food Facts (§5.2) — et c'est désormais la seule chose
   * qu'elle serve à espacer.
   */
  const search = useQuery({
    queryKey: ['food-search', debounced],
    queryFn: () => searchFoods(debounced),
    enabled: scope === 'food' && debounced.length >= MIN_QUERY_LENGTH,
    /*
     * Une même recherche relancée dans la minute ressort du cache : c'est autant
     * d'appels épargnés à Open Food Facts (§5.2).
     *
     * ⚠️ **Sauf si OFF était injoignable.** Un échec ne se met pas en cache :
     * sinon, retaper le même mot pendant une minute reservirait le même
     * « Open Food Facts injoignable » sans jamais réessayer — constaté au banc,
     * OFF ayant refusé une recherche en cours de test. La moitié personnelle du
     * résultat, elle, est toujours correcte : c'est bien la seule moitié
     * distante qu'il faut redemander.
     */
    staleTime: (query) => (query.state.data?.offStatus === 'unavailable' ? 0 : 60_000),
  });

  const data = search.data;
  const searching = scope === 'food' && debounced.length >= MIN_QUERY_LENGTH && search.isFetching;
  const off = data?.off ?? [];
  /*
   * 🔴 **Le seul endroit où l'absence de réseau se dit.** `offStatus` vaut
   * `unavailable` quand le serveur n'a pas pu joindre OFF ; une requête qui n'a
   * jamais abouti (hors ligne) n'a pas de `data` du tout — les deux cas
   * s'affichent pareil, parce qu'ils veulent dire la même chose à
   * l'utilisateur : « la moitié distante manque, la vôtre est là ».
   *
   * ⚠️ Jamais présenté comme une erreur (§3.5 : « ne jamais présenter “hors
   * ligne” comme une erreur — en V2 c'est un mode nominal »).
   */
  const offIndisponible =
    scope === 'food' &&
    debounced.length >= MIN_QUERY_LENGTH &&
    !search.isFetching &&
    (data === undefined || data.offStatus === 'unavailable');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <input
        value={term}
        onChange={(event) => {
          setTerm(event.target.value);
        }}
        placeholder={scope === 'food' ? 'Nom d’un aliment…' : 'Nom ou tag d’une recette…'}
        autoComplete="off"
        autoCapitalize="off"
        autoCorrect="off"
        spellCheck={false}
        className="ui-input"
        style={{ width: '100%' }}
      />

      {cherche === '' && (
        <Hint>
          {scope === 'food'
            ? 'Votre base répond dès la première lettre, sans réseau. Au-delà de trois, Open Food Facts est interrogé en plus.'
            : 'La recherche porte sur le nom et sur les tags, sans tenir compte des accents.'}
        </Hint>
      )}

      {/*
        ⚠️ **Une erreur de cette requête n'est plus bloquante du tout.** Elle ne
        concerne que la moitié Open Food Facts ; la moitié personnelle est déjà
        affichée au-dessus. Hors ligne on ne montre donc rien ici — c'est
        `offIndisponible` qui parle, en note neutre.
      */}
      {search.isError && search.data !== undefined && <ErrorNote>{search.error.message}</ErrorNote>}

      {cherche !== '' && (
        <>
          {/* Recettes seules sous la catégorie « Recette » ; aliments seuls
              sous « Aliment ». La base personnelle prime toujours sur OFF (§7.2). */}
          {scope === 'recipe' && localRecipes.length > 0 && <Heading>Mes recettes</Heading>}
          {scope === 'recipe' &&
            localRecipes.map((recipe) => (
              <PersonalRecipeRow
                key={recipe.id}
                recipe={recipe}
                onPick={() => {
                  onPick({ kind: 'recipe', recipe });
                }}
              />
            ))}

          {scope === 'food' && personal.length > 0 && <Heading>Ma base</Heading>}
          {scope === 'food' &&
            personal.map((food) => (
              <PersonalFoodRow
                key={food.id}
                food={food}
                onPick={() => {
                  onPick({ kind: 'food', food });
                }}
              />
            ))}

          {scope === 'food' && off.length > 0 && <Heading>Open Food Facts</Heading>}
          {scope === 'food' &&
            off.map((product) => (
              <OffFoodRow
                key={product.code}
                product={product}
                onPick={() => {
                  onPick({ kind: 'off', product });
                }}
              />
            ))}

          {offIndisponible && (
            <Hint>
              Open Food Facts n’est pas joignable : seuls vos aliments sont proposés.
            </Hint>
          )}

          {/*
            Vide au sens de **la catégorie courante** : sous « Recette », des
            résultats d'aliments ne comptent pas — ils ne sont pas affichés.
            ⚠️ Et « créer cet aliment » n'est proposé que sous « Aliment » : une
            recette ne se crée pas depuis l'écran d'ajout, elle a besoin de ses
            ingrédients (§6.5), donc de la Bibliothèque.
          */}
          {(scope === 'food' ? personal.length === 0 && off.length === 0 : localRecipes.length === 0) &&
            !searching && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <Hint>Aucun résultat pour « {cherche} ».</Hint>
                {scope === 'food' ? (
                  <Button variant="ghost" shape="rounded" block onClick={onCreate}>
                    Créer cet aliment dans ma base
                  </Button>
                ) : (
                  <Hint>
                    Les recettes se créent dans la Bibliothèque : il leur faut des ingrédients.
                  </Hint>
                )}
              </div>
            )}
        </>
      )}

      {searching && <Hint>Recherche…</Hint>}
    </div>
  );
}
